// src/utils/peerHelpers.js
// Better-than-FAANG+ WebRTC helpers for JG Universe
// Track-first, session-based, aligned with CallContext v3, with DEBUG logs.

import { isSamsungGalaxy } from './deviceUtils';

// ==============================
//  ICE / TURN configuration
// ==============================
function getIceServers() {
  const servers = [
    {
      urls: [
        'stun:stun.l.google.com:19302',
        'stun:global.stun.twilio.com:3478',
      ],
    },
  ];

  const turnUrl = process.env.REACT_APP_TURN_URL;
  const turnUser = process.env.REACT_APP_TURN_USERNAME;
  const turnCred = process.env.REACT_APP_TURN_CREDENTIAL;

  if (turnUrl && turnUser && turnCred) {
    servers.push({
      urls: turnUrl.split(',').map((u) => u.trim()),
      username: turnUser,
      credential: turnCred,
    });
  }

  return servers;
}

const FORCE_RELAY = /^(1|true|yes)$/i.test(
  String(process.env.REACT_APP_RTC_FORCE_RELAY || ''),
);

export function getRTCConfig() {
  const cfg = {
    iceServers: getIceServers(),
    iceTransportPolicy: FORCE_RELAY ? 'relay' : 'all',
  };
  console.log('[peerHelpers] getRTCConfig →', cfg);
  return cfg;
}

// ==============================
//  Media helpers
// ==============================
let localStreamRef = null;

/**
 * ensureLocalMedia
 *
 * Backwards compatible:
 *   - ensureLocalMedia(true)          → audio + video
 *   - ensureLocalMedia(false)         → audio-only
 *   - ensureLocalMedia({audio,video}) → explicit
 */
export async function ensureLocalMedia(opts = false) {
  if (localStreamRef) {
    console.log(
      '[peerHelpers] ensureLocalMedia → reusing existing stream, tracks:',
      localStreamRef.getTracks().map((t) => `${t.kind}:${t.readyState}`),
    );
    return localStreamRef;
  }

  let wantVideoIntent = false;
  let baseConstraints;

  if (typeof opts === 'object' && opts !== null) {
    const { audio = true, video = false } = opts;
    wantVideoIntent = !!video;

    baseConstraints = {
      audio: audio
        ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : false,
      video: video
        ? {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, max: 60 },
          }
        : false,
    };
  } else {
    // Legacy boolean signature
    wantVideoIntent = !!opts;
    baseConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: wantVideoIntent
        ? {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, max: 60 },
          }
        : false,
    };
  }

  // Samsung-specific tweaks when video is requested
  let constraints = baseConstraints;
  if (wantVideoIntent && isSamsungGalaxy() && baseConstraints.video) {
    constraints = {
      ...baseConstraints,
      video: {
        ...baseConstraints.video,
        width: baseConstraints.video.width || { ideal: 1280 },
        height: baseConstraints.video.height || { ideal: 720 },
        aspectRatio: baseConstraints.video.aspectRatio || 1.7777777778,
        facingMode: baseConstraints.video.facingMode || 'user',
      },
    };
  }

  console.log('[peerHelpers] ensureLocalMedia → constraints', constraints);

  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  console.log(
    '[peerHelpers] ensureLocalMedia → got stream tracks:',
    stream.getTracks().map((t) => `${t.kind}:${t.readyState}`),
  );
  localStreamRef = stream;
  return stream;
}

export function getLocalStream() {
  console.log(
    '[peerHelpers] getLocalStream →',
    localStreamRef
      ? localStreamRef.getTracks().map((t) => `${t.kind}:${t.readyState}`)
      : 'null',
  );
  return localStreamRef;
}

// ==============================
//  Safari codec preference
// ==============================
function preferCodecsForSafari(pc) {
  try {
    const trs = pc.getTransceivers ? pc.getTransceivers() : [];
    console.log(
      '[peerHelpers] preferCodecsForSafari → transceivers:',
      trs.map((tr) => ({
        mid: tr.mid,
        currentDirection: tr.currentDirection,
        direction: tr.direction,
        kind: tr.receiver?.track?.kind || tr.kind,
      })),
    );
    trs.forEach((tr) => {
      if (!tr.setCodecPreferences || !RTCRtpSender.getCapabilities) return;
      const kind = tr.receiver?.track?.kind || tr.kind;
      const caps = RTCRtpSender.getCapabilities(kind);
      if (!caps?.codecs?.length) return;

      const isVideo = kind === 'video';
      const preferred = caps.codecs
        .filter((c) =>
          isVideo ? /H264/i.test(c.mimeType) : /opus/i.test(c.mimeType),
        )
        .concat(
          caps.codecs.filter(
            (c) => !(isVideo ? /H264/i : /opus/i).test(c.mimeType),
          ),
        );

      tr.setCodecPreferences(preferred);
    });
  } catch (e) {
    console.warn('[peerHelpers] preferCodecsForSafari error', e);
  }
}

// ==============================
//  Session-based API (track-first)
// ==============================
export function createPeerSession({
  wantVideo = false,
  onRemoteStream,
  onConnectionState,
} = {}) {
  const cfg = getRTCConfig();
  const pc = new RTCPeerConnection(cfg);

  console.log('[peerHelpers] createPeerSession → new RTCPeerConnection', {
    wantVideo,
    cfg,
  });

  let pendingCandidates = [];
  let remoteDescriptionSet = false;
  let remoteStream = new MediaStream();

  // --- helper: assemble remote stream from receivers (fallback if ontrack is flaky) ---
  const refreshRemoteFromReceivers = () => {
    try {
      const receivers = pc.getReceivers ? pc.getReceivers() : [];
      console.log(
        '[peerHelpers] refreshRemoteFromReceivers → receivers:',
        receivers.map((r) => ({
          kind: r.track?.kind,
          id: r.track?.id,
          readyState: r.track?.readyState,
        })),
      );

      receivers.forEach((r) => {
        const track = r.track;
        if (!track || track.readyState === 'ended') return;
        if (!remoteStream.getTracks().some((t) => t.id === track.id)) {
          console.log(
            '[peerHelpers] refreshRemoteFromReceivers → adding track to remoteStream:',
            track.kind,
            track.id,
          );
          remoteStream.addTrack(track);
        }
      });

      if (remoteStream.getTracks().length > 0) {
        console.log(
          '[peerHelpers] refreshRemoteFromReceivers → remoteStream tracks:',
          remoteStream.getTracks().map((t) => `${t.kind}:${t.id}`),
        );
        onRemoteStream?.(remoteStream);
      } else {
        console.log(
          '[peerHelpers] refreshRemoteFromReceivers → no tracks to publish yet',
        );
      }
    } catch (e) {
      console.warn('[peerHelpers] refreshRemoteFromReceivers error', e);
    }
  };

  // --- track-first: ontrack wired immediately ---
  pc.ontrack = (event) => {
    try {
      console.log(
        '[peerHelpers] pc.ontrack fired →',
        event.track?.kind,
        'streams:',
        event.streams?.length,
      );

      const streams =
        event.streams && event.streams.length ? event.streams : [remoteStream];

      streams.forEach((s) => {
        s.getTracks().forEach((t) => {
          if (!remoteStream.getTracks().some((x) => x.id === t.id)) {
            console.log(
              '[peerHelpers] ontrack → adding track to remoteStream:',
              t.kind,
              t.id,
            );
            remoteStream.addTrack(t);
          }
        });
      });

      console.log(
        '[peerHelpers] ontrack → remoteStream tracks now:',
        remoteStream.getTracks().map((t) => `${t.kind}:${t.id}`),
      );

      onRemoteStream?.(remoteStream);
    } catch (e) {
      console.warn('[peerHelpers] ontrack error', e);
    }
  };

  // --- connection state sync + fallback remote assembly ---
  const syncConnectionState = () => {
    const cs = pc.connectionState || pc.iceConnectionState;
    console.log('[peerHelpers] connection state changed →', cs);
    onConnectionState?.(cs);

    // If we hit connected but somehow missed ontrack,
    // assemble remoteStream from receivers.
    if (cs === 'connected' && remoteStream.getTracks().length === 0) {
      console.log(
        '[peerHelpers] connectionState=connected but remoteStream empty → running refreshRemoteFromReceivers()',
      );
      refreshRemoteFromReceivers();
    }
  };

  pc.onconnectionstatechange = syncConnectionState;
  pc.oniceconnectionstatechange = syncConnectionState;

  // --- normalize transceivers to sendrecv (without double-creating them) ---
  const normalizeDirections = () => {
    try {
      const trs = pc.getTransceivers ? pc.getTransceivers() : [];
      trs.forEach((tr) => {
        if (!tr) return;
        if (tr.direction !== 'sendrecv') {
          console.log(
            '[peerHelpers] normalizeDirections → forcing sendrecv for transceiver',
            {
              mid: tr.mid,
              prev: tr.direction,
              kind: tr.receiver?.track?.kind || tr.kind,
            },
          );
          try {
            tr.direction = 'sendrecv';
          } catch (e) {
            console.warn(
              '[peerHelpers] normalizeDirections unable to set direction',
              e,
            );
          }
        }
      });
    } catch (e) {
      console.warn('[peerHelpers] normalizeDirections error', e);
    }
  };

  pc.onnegotiationneeded = () => {
    console.log('[peerHelpers] onnegotiationneeded fired');
    normalizeDirections();
    preferCodecsForSafari(pc);
  };

  // Helper to drain early ICE once remoteDescription is set
  const flushPendingCandidates = async () => {
    if (!remoteDescriptionSet || !pendingCandidates.length) return;
    const queue = [...pendingCandidates];
    pendingCandidates = [];
    console.log(
      '[peerHelpers] flushPendingCandidates → applying',
      queue.length,
      'candidates',
    );
    for (const c of queue) {
      try {
        await pc.addIceCandidate(c);
      } catch (e) {
        console.warn('[peerHelpers] addIceCandidate (flush) error', e);
      }
    }
  };

  return {
    pc,

    getRemoteStream() {
      console.log(
        '[peerHelpers] getRemoteStream → tracks:',
        remoteStream.getTracks().map((t) => `${t.kind}:${t.id}`),
      );
      return remoteStream;
    },

    async addLocalStream(stream) {
      if (!stream) {
        console.warn('[peerHelpers] addLocalStream called with null stream');
        return;
      }
      console.log(
        '[peerHelpers] addLocalStream → tracks:',
        stream.getTracks().map((t) => `${t.kind}:${t.readyState}`),
      );

      try {
        const existingTransceivers = pc.getTransceivers
          ? pc.getTransceivers()
          : [];

        stream.getTracks().forEach((track) => {
          const kind = track.kind;

          // Try to reuse an existing transceiver for this kind
          const matching = existingTransceivers.find((tr) => {
            const sendKind = tr.sender?.track?.kind;
            const recvKind = tr.receiver?.track?.kind;
            return sendKind === kind || recvKind === kind || tr.kind === kind;
          });

          if (matching && matching.sender) {
            console.log(
              '[peerHelpers] addLocalStream → reusing transceiver for',
              kind,
              '→ replaceTrack',
            );
            try {
              matching.direction = 'sendrecv';
            } catch (e) {}
            matching.sender.replaceTrack(track);
          } else {
            console.log(
              '[peerHelpers] addLocalStream → addTrack for',
              kind,
              track.id,
            );
            pc.addTrack(track, stream);
          }
        });
      } catch (e) {
        console.warn('[peerHelpers] addLocalStream error', e);
      }

      normalizeDirections();
      preferCodecsForSafari(pc);
    },

    async createOffer() {
      console.log('[peerHelpers] createOffer called');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });

      console.log(
        '[peerHelpers] createOffer → got offer, setting localDescription',
      );
      preferCodecsForSafari(pc);
      await pc.setLocalDescription(offer);

      return { type: offer.type, sdp: offer.sdp };
    },

    async setRemoteOfferAndCreateAnswer(remoteOfferPayload) {
      console.log(
        '[peerHelpers] setRemoteOfferAndCreateAnswer incoming payload:',
        remoteOfferPayload,
      );

      let offerObj = null;
      if (remoteOfferPayload?.offer) {
        offerObj = remoteOfferPayload.offer;
      } else if (remoteOfferPayload?.type && remoteOfferPayload?.sdp) {
        offerObj = remoteOfferPayload;
      } else {
        console.error(
          '[peerHelpers] [FATAL] invalid offer payload:',
          remoteOfferPayload,
        );
        throw new Error(
          'Invalid remote offer: expected payload.offer or {type,sdp}',
        );
      }

      console.log(
        '[peerHelpers] setRemoteOfferAndCreateAnswer → using offer:',
        offerObj,
      );

      const offerDesc =
        offerObj instanceof RTCSessionDescription
          ? offerObj
          : new RTCSessionDescription({
              type: offerObj.type,
              sdp: offerObj.sdp,
            });

      await pc.setRemoteDescription(offerDesc);
      remoteDescriptionSet = true;
      console.log(
        '[peerHelpers] setRemoteOfferAndCreateAnswer → remoteDescription set, flushing candidates',
      );

      await flushPendingCandidates();

      const answer = await pc.createAnswer();
      console.log(
        '[peerHelpers] setRemoteOfferAndCreateAnswer → created answer, setting localDescription',
      );
      preferCodecsForSafari(pc);
      await pc.setLocalDescription(answer);

      return { type: answer.type, sdp: answer.sdp };
    },

    async setRemoteAnswer(remoteAnswer) {
      if (!pc) throw new Error('PeerConnection not initialized');
      console.log(
        '[peerHelpers] setRemoteAnswer incoming payload:',
        remoteAnswer,
      );

      let desc;

      try {
        if (remoteAnswer instanceof RTCSessionDescription) {
          desc = remoteAnswer;
        } else if (remoteAnswer && remoteAnswer.sdp && remoteAnswer.type) {
          desc = new RTCSessionDescription({
            type: remoteAnswer.type,
            sdp: remoteAnswer.sdp,
          });
        } else if (
          remoteAnswer &&
          remoteAnswer.answer &&
          remoteAnswer.answer.sdp
        ) {
          desc = new RTCSessionDescription({
            type: remoteAnswer.answer.type || 'answer',
            sdp: remoteAnswer.answer.sdp,
          });
        } else if (typeof remoteAnswer === 'string') {
          desc = new RTCSessionDescription({
            type: 'answer',
            sdp: remoteAnswer,
          });
        } else {
          console.error(
            '[peerHelpers] Invalid remoteAnswer format:',
            remoteAnswer,
          );
          throw new Error('Invalid remote answer format');
        }
      } catch (e) {
        console.error(
          '[peerHelpers] Failed to normalize remoteAnswer:',
          remoteAnswer,
          e,
        );
        throw e;
      }

      await pc.setRemoteDescription(desc);
      remoteDescriptionSet = true;
      console.log(
        '[peerHelpers] setRemoteAnswer → remoteDescription set, flushing candidates',
      );
      await flushPendingCandidates();

      // Extra safety: assemble remote stream from receivers after answer
      console.log(
        '[peerHelpers] setRemoteAnswer → calling refreshRemoteFromReceivers as safety net',
      );
      refreshRemoteFromReceivers();
    },

    onLocalIceCandidate(cb) {
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          console.log(
            '[peerHelpers] onicecandidate → local candidate:',
            e.candidate.type,
            e.candidate.protocol,
            e.candidate.address,
          );
          cb?.(e.candidate);
        } else {
          console.log('[peerHelpers] onicecandidate → end of candidates');
        }
      };
    },

    async addRemoteIceCandidate(candidate) {
      if (!candidate) {
        console.warn(
          '[peerHelpers] addRemoteIceCandidate called with null candidate',
        );
        return;
      }

      if (remoteDescriptionSet && pc.remoteDescription) {
        console.log(
          '[peerHelpers] addRemoteIceCandidate → applying immediately',
          candidate.type,
          candidate.protocol,
          candidate.address,
        );
        try {
          await pc.addIceCandidate(candidate);
        } catch (e) {
          console.warn('[peerHelpers] addRemoteIceCandidate error', e);
        }
      } else {
        console.log(
          '[peerHelpers] addRemoteIceCandidate → queueing until remoteDescription set',
        );
        pendingCandidates.push(candidate);
      }
    },

    close(localStream) {
      console.log('[peerHelpers] close called → tearing down media + pc');
      try {
        localStream?.getTracks?.().forEach((t) => t.stop());
      } catch {
        // ignore
      }
      try {
        pc.getSenders().forEach((s) => s.track && s.track.stop());
      } catch {
        // ignore
      }
      try {
        pc.close();
      } catch {
        // ignore
      }
      onConnectionState?.('closed');

      if (localStreamRef) {
        try {
          localStreamRef.getTracks().forEach((t) => t.stop());
        } catch {
          // ignore
        }
        localStreamRef = null;
      }
    },
  };
}
